import { StageBadge } from "./StageBadge";
import { formatIso, type HistoryEntry } from "../types";
import {
  computeStageReconciliation,
  parseOnboardingRequirements,
  parseOnboardingTimeline,
  type OnboardingRequirements,
  type StageReconciliation,
} from "./lifecycle-sheet-helpers";

interface OnboardingSpineProps {
  trackStage: string | null;
  mirrorStage: string | null;
  history: unknown;
  metadata?: Record<string, unknown>;
}

export function OnboardingSpine({
  trackStage,
  mirrorStage,
  history,
  metadata,
}: OnboardingSpineProps) {
  const reconciliation = computeStageReconciliation(trackStage, mirrorStage);
  const requirements = parseOnboardingRequirements(metadata);
  const timeline = parseOnboardingTimeline(history);

  return (
    <div className="mb-6">
      <div className="mb-3 flex items-center gap-2">
        <span className="text-muted-foreground text-sm">Onboarding:</span>
        <StageBadge stage={reconciliation.recordValue} />
      </div>
      <ReconciliationRow reconciliation={reconciliation} />
      {requirements && <RequirementsBlock requirements={requirements} />}
      <OnboardingTimeline entries={timeline} />
    </div>
  );
}

function RequirementsBlock({
  requirements,
}: {
  requirements: OnboardingRequirements;
}) {
  return (
    <div className="mb-4">
      <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
        Outstanding requirements
      </h4>
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
        <ul className="space-y-0.5">
          {requirements.currentlyDue.map((item) => (
            <li key={item} className="text-foreground font-mono text-xs">
              {item}
            </li>
          ))}
        </ul>
        {requirements.deadline !== null && (
          <p className="mt-1.5 text-xs font-medium text-amber-700">
            Deadline: {formatEpochSeconds(requirements.deadline)}
          </p>
        )}
      </div>
    </div>
  );
}

function formatEpochSeconds(seconds: number): string {
  return new Date(seconds * 1000).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function ReconciliationRow({
  reconciliation,
}: {
  reconciliation: StageReconciliation;
}) {
  const indicator =
    reconciliation.status === "in_sync"
      ? { text: "✓ in sync", className: "text-green-700" }
      : reconciliation.status === "drift"
        ? { text: "⚠ drift", className: "text-red-700" }
        : { text: "no track yet", className: "text-subtle-foreground" };

  return (
    <div className="border-border bg-muted mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border px-3 py-2 text-xs">
      <span className="text-muted-foreground">
        record:{" "}
        <span className="text-foreground font-mono">
          {reconciliation.recordValue}
        </span>
      </span>
      <span className="text-muted-foreground">
        mirror:{" "}
        <span className="text-foreground font-mono">
          {reconciliation.mirrorValue}
        </span>
      </span>
      <span className={`font-medium ${indicator.className}`}>
        {indicator.text}
      </span>
    </div>
  );
}

function OnboardingTimeline({ entries }: { entries: HistoryEntry[] }) {
  return (
    <div>
      <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
        Onboarding timeline
      </h4>
      {entries.length === 0 ? (
        <p className="text-subtle-foreground text-sm">No transitions yet.</p>
      ) : (
        <div className="divide-border border-border divide-y rounded-md border">
          {entries.map((entry, idx) => (
            <div key={idx} className="px-4 py-2.5">
              <div className="text-foreground flex items-center gap-1.5 text-sm font-medium">
                <span className="text-muted-foreground font-mono">
                  {entry.from}
                </span>
                <span className="text-subtle-foreground">&#8594;</span>
                <span className="font-mono">{entry.to}</span>
              </div>
              <div className="text-subtle-foreground mt-0.5 text-xs">
                {formatIso(entry.timestamp)}
                {entry.triggeredBy ? ` · ${entry.triggeredBy}` : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
